import { useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ExpensablesDataTableDocument,
  MyGroupedExpensablesDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  BulkRemoveExpensablesFromGroupDocument,
  ExpensablesDataCollectionDocument,
  GetGroupsDocument,
  MyGroupedExpensablesDataCollectionDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useDelete } from 'modules/expenses/hooks/useExpensable'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { canRemoveExpensableFromGroup } from 'modules/expenses/lib/expensables/availableActions'
import { Expensable } from 'modules/expenses/types/Expensable'

import { buildErrorMessages } from '../helpers/ActionErrorMessageHandler'

interface DeleteButtonsProps {
  expensable: Expensable
  canDeleteExpensable: boolean
  onClose: () => void
  onLoadingChange?: (loading: boolean) => void
  disabled?: boolean
}

const useRemoveFromGroup = () => {
  const [removeExpensablesFromGroup, { loading }] = useMutation(
    BulkRemoveExpensablesFromGroupDocument,
    {
      extractorFn: (data) => data.expensesMutations.bulkRemoveFromGroupExpensesExpensable,
      refetchQueries: [
        ExpensablesDataTableDocument,
        ExpensablesDataCollectionDocument,
        GetGroupsDocument,
        MyGroupedExpensablesDataTableDocument,
        MyGroupedExpensablesDataCollectionDocument,
      ],
    }
  )

  return {
    loading,
    removeFromGroup: (ids: number[]) => removeExpensablesFromGroup({ variables: { ids } }),
  }
}

const DeleteButtons = ({
  expensable,
  canDeleteExpensable,
  onClose,
  onLoadingChange,
  disabled,
}: DeleteButtonsProps) => {
  const { delete: destroy, loading } = useDelete()
  const { removeFromGroup } = useRemoveFromGroup()
  const { section } = useOutletContext<RouteContextParams>()

  useEffect(() => {
    onLoadingChange?.(loading)
  }, [loading, onLoadingChange])

  const deleteExpensable = async () => {
    const result = await destroy({ id: expensable.id })

    const handled = handleResult(result, (errors) => {
      // Need to format the errors so that it can be passed to the setFlash
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })
    if (!handled.success) return handled

    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.modals.new_expense.deleted_successfully'),
    })
    onClose()
  }
  const removeExpensableFromGroup = async () => {
    const result = await removeFromGroup([expensable.id])
    const handled = handleResult(result, (errors) => {
      // Need to format the errors so that it can be passed to the setFlash
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })
    if (!handled.success) return handled
    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.success_messages.all_items'),
    })

    onClose()
  }

  const removeButton = () => {
    if (!canDeleteExpensable) return null

    return (
      <Button
        onClick={deleteExpensable}
        type="secondary"
        disabled={disabled}
        loading={loading}
        iconName={loading ? 'upload' : undefined}
      >
        {i18n.t('expenses_v2.buttons.delete')}
      </Button>
    )
  }

  const removeOptionsMenu = () => {
    return (
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary">
            {i18n.t('expenses_v2.buttons.more')}
          </Button.Decorative>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="top" align="start">
          <PopoverMenu.TouchableOption
            title={i18n.t('expenses_v2.buttons.remove_from_group')}
            onClick={removeExpensableFromGroup}
          />
          {canDeleteExpensable && (
            <PopoverMenu.TouchableOption
              danger
              title={i18n.t('expenses_v2.buttons.delete')}
              onClick={deleteExpensable}
            />
          )}
        </PopoverMenu.Content>
      </PopoverMenu>
    )
  }
  return canRemoveExpensableFromGroup(expensable, section) ? removeOptionsMenu() : removeButton()
}

export default DeleteButtons
